<template>
  <div class="relative flex" :class="{ isInHomeScreen: isInHomeScreen }">
    <span v-if="showInQueueBadge" :class="$dm('badge', 'badge badge--dark')">
      Queued
    </span>
    <button
      v-if="shouldShowExpandBtn"
      role="button"
      aria-label="Expand"
      class="actions expand-btn flex items-center p-1 mr-1"
      @click="toggleExpanded"
    >
      <fluent-icon
        :icon="isWidgetExpanded ? 'collapse' : 'expand'"
        size="22"
        view-box="0 0 28 28"
        :class="$dm('text-black-900', 'dark:text-slate-50')"
      />
    </button>
    <button
      v-if="getMenuIcon()"
      role="button"
      aria-label="Menu"
      class="actions flex items-center p-1"
      @click="checkAndRunCorrectFn"
    >
      <fluent-icon
        :id="getMenuId()"
        :icon="getMenuIcon()"
        size="22"
        :class="$dm('text-black-900', 'dark:text-slate-50')"
      />
    </button>
    <div
      v-if="isHeaderMenuOpen"
      id="popover-header"
      class="absolute right-0 rounded-xl z-10 popover shadow-2xl"
      :class="$dm('bg-white', 'dark:bg-slate-950 darkPopover')"
    >
      <button
        v-if="!hideMinimizeButton"
        class="button transparent compact close-button"
        :class="{
          'rn-close-button': shouldShowCloseBtn,
        }"
        @click="closeWindow"
      >
        <fluent-icon icon="minimize" size="20" />
        {{ $t('HEAD_ACTIONS.MINIMIZE') }}
      </button>
      <button
        v-if="showEmailTranscriptButton"
        class="button transparent compact"
        @click="sendTranscript"
      >
        <fluent-icon icon="transcript" size="20" />
        {{ $t('HEAD_ACTIONS.SEND_TRANSCRIPT') }}
      </button>
      <button
        v-if="shouldShowPopoutButton"
        class="button transparent compact new-window--button"
        @click="popoutWindow"
      >
        <fluent-icon icon="external" size="20" />
        {{ $t('HEAD_ACTIONS.OPEN_IN_NEW_WINDOW') }}
      </button>
      <button
        v-if="showEndConversationButton"
        class="button transparent compact end-conversation--button"
        :title="$t('END_CONVERSATION')"
        @click="resolveConversation"
      >
        <fluent-icon icon="exit" size="20" />
        {{ $t('HEAD_ACTIONS.END_CONVERSATION') }}
      </button>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { IFrameHelper, RNHelper } from 'widget/helpers/utils';
import { popoutChatWindow } from '../helpers/popoutHelper';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';
import darkModeMixin from 'widget/mixins/darkModeMixin';
import configMixin from 'widget/mixins/configMixin';
import { CONVERSATION_STATUS } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { sendEmailTranscript } from 'widget/api/conversation';
import { sendMessage } from '../helpers/utils';

export default {
  name: 'HeaderActions',
  components: { FluentIcon },
  mixins: [configMixin, darkModeMixin],
  props: {
    showPopoutButton: {
      type: Boolean,
      default: false,
    },
    isInMessageScreen: {
      type: Boolean,
      default: false,
      required: false,
    },
    isInHomeScreen: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data: () => ({
    messageEventListener: null,
  }),
  computed: {
    ...mapGetters({
      conversationSize: 'conversation/getConversationSize',
      conversationAttributes: 'conversationAttributes/getConversationParams',
      currentUser: 'contacts/getCurrentUser',
      isWidgetExpanded: 'appConfig/isWidgetExpanded',
      isMobile: 'appConfig/isMobile',
      allowWidgetMaximize: 'appConfig/getAllowWidgetMaximize',
      hideMessageBubble: 'appConfig/getHideMessageBubble',
      isHeaderMenuOpen: 'appConfig/isHeaderMenuOpen',
    }),
    canLeaveConversation() {
      return [
        CONVERSATION_STATUS.OPEN,
        CONVERSATION_STATUS.SNOOZED,
        CONVERSATION_STATUS.PENDING,
      ].includes(this.conversationStatus);
    },
    isIframe() {
      return IFrameHelper.isIFrame();
    },
    shouldShowExpandBtn() {
      if (this.isMobile) return false;
      if (!window.parent) return false;
      let settingsObject = {};
      try {
        settingsObject = JSON.parse(window.hooryWebChannel.widget_settings);
        // eslint-disable-next-line no-empty
      } catch (e) {}

      if (this.allowWidgetMaximize === 'disabled') return false;
      if (settingsObject.allowWidgetMaximize === 'disabled') return false;
      try {
        return window.location !== window.parent.location;
      } catch (error) {
        return false;
      }
    },
    shouldShowCloseBtn() {
      return (
        (RNHelper.isRNWebView() || this.isMobile || this.hideMessageBubble) &&
        !this.hideMinimizeButton
      );
    },
    showEndConversationButton() {
      return (
        this.canLeaveConversation &&
        this.hasEndConversationEnabled &&
        this.isInMessageScreen
      );
    },
    showEmailTranscriptButton() {
      return (
        !!this.currentUser &&
        !!this.currentUser.email &&
        !!this.conversationSize &&
        this.isInMessageScreen
      );
    },
    shouldShowPopoutButton() {
      return !this.isMobile && this.isIframe;
    },
    conversationStatus() {
      return this.conversationAttributes.status;
    },
    hasWidgetOptions() {
      return this.showPopoutButton || this.conversationStatus === 'open';
    },
    showInQueueBadge() {
      return (
        this.isInMessageScreen &&
        this.conversationAttributes.status &&
        this.conversationAttributes.in_queue
      );
    },
  },
  mounted() {
    this.messageEventListener = event => {
      this.setHeaderMenuOpen(false, event);
    };
    window.addEventListener('message', this.messageEventListener);
    document.addEventListener('click', this.close);
  },
  beforeDestroy() {
    this.setHeaderMenuOpen(false);
    window.removeEventListener('message', this.messageEventListener);
    document.removeEventListener('click', this.close);
  },
  methods: {
    setHeaderMenuOpen(isOpen, event = null) {
      if (event) {
        if (event.source !== window.parent) return;
        if (event.data.type === 'liveChatWidgetClosed') {
          this.$store.dispatch('appConfig/setHeaderMenuOpen', isOpen);
        }
      } else {
        this.$store.dispatch('appConfig/setHeaderMenuOpen', isOpen);
      }
    },
    isNoMenuThere() {
      return (
        !this.shouldShowCloseBtn &&
        !this.showEmailTranscriptButton &&
        !this.showEndConversationButton &&
        !this.shouldShowPopoutButton
      );
    },
    onlyCloseVisible() {
      return (
        this.shouldShowCloseBtn &&
        !this.showEmailTranscriptButton &&
        !this.showEndConversationButton &&
        !this.shouldShowPopoutButton
      );
    },
    onlyPopoutVisible() {
      return (
        this.shouldShowPopoutButton &&
        !this.shouldShowCloseBtn &&
        !this.showEndConversationButton &&
        !this.showEmailTranscriptButton
      );
    },
    checkAndRunCorrectFn() {
      if (this.onlyCloseVisible()) {
        this.closeWindow(true);
      } else if (this.onlyPopoutVisible()) {
        this.popoutWindow(true);
      } else {
        window.logger.logUserAction('info', '3 dots header btn clicked');
        this.toggleMenu();
      }
    },
    toggleMenu() {
      this.setHeaderMenuOpen(!this.isHeaderMenuOpen);
    },
    getMenuId() {
      const menuIcon = this.getMenuIcon() || 'missing';
      return `icon-${menuIcon}`;
    },
    getMenuIcon() {
      if (this.isNoMenuThere()) {
        return '';
      }
      if (this.onlyCloseVisible()) {
        return 'minimize';
      }
      if (this.onlyPopoutVisible()) {
        return 'external';
      }
      return 'dots';
    },
    toggleExpanded() {
      window.logger.logUserAction('info', 'Expand button clicked');
      const isExpanded = !this.isWidgetExpanded;
      this.$store.dispatch('appConfig/setWidgetExpanded', isExpanded);
      sendMessage({
        event: 'toggleExpand',
        expandWidget: isExpanded,
      });
    },
    close(e) {
      if (!this.$el.contains(e.target)) {
        this.setHeaderMenuOpen(false);
      }
    },
    async sendTranscript() {
      window.logger.logUserAction('info', 'Transcript button clicked');
      const { email } = this.currentUser;
      if (email) {
        try {
          await sendEmailTranscript({
            email,
          });
          window.bus.$emit(BUS_EVENTS.SHOW_ALERT, {
            message: this.$t('EMAIL_TRANSCRIPT.SEND_EMAIL_SUCCESS'),
            type: 'success',
          });
        } catch (error) {
          window.bus.$emit(BUS_EVENTS.SHOW_ALERT, {
            message: this.$t('EMAIL_TRANSCRIPT.SEND_EMAIL_ERROR'),
          });
        }
      }
      this.setHeaderMenuOpen(false);
    },
    popoutWindow(forced = false) {
      window.logger.logUserAction('info', 'Popout button clicked');
      if (!forced) this.closeWindow();
      const {
        location: { origin },
        hooryWebChannel: { websiteToken },
        authToken,
      } = window;
      popoutChatWindow(
        origin,
        websiteToken,
        this.$root.$i18n.locale,
        authToken
      );
    },
    closeWindow(forced = false) {
      window.logger.logUserAction('info', 'Minimize clicked');

      if (!forced) this.toggleMenu();
      if (IFrameHelper.isIFrame()) {
        IFrameHelper.sendMessage({ event: 'closeWindow' });
      } else if (RNHelper.isRNWebView) {
        RNHelper.sendMessage({ type: 'close-widget' });
      }
    },
    resolveConversation() {
      this.toggleMenu();
      this.$store.dispatch('conversation/resolveConversation');
    },
  },
};
</script>
<style scoped lang="scss">
@import '~widget/assets/scss/variables.scss';

.isInHomeScreen {
  position: absolute;
  right: 14px;
  top: 8px;
}
.popover {
  border: 1px solid #f0f0f5;
  top: 50px;
  padding: 8px 12px;
  width: 254px;

  > button {
    display: flex !important;
    flex-direction: row;
    width: 100%;
    text-align: left;
    padding: 8px 12px 8px 0;
    color: #0f0a0a;
    margin-bottom: 8px;
    gap: 8px;
    align-items: center;
    &:hover {
      color: #6c54f6;
    }
    &:last-child {
      margin-bottom: 0;
    }
  }
  &.darkPopover {
    button {
      color: #fff;
      &:hover {
        color: #6c54f6;
      }
    }
  }
}

.end-conversation--button {
  color: #ed5e80 !important;
}

span {
  color: $color-heading;
  font-size: $font-size-large;
}

.close-button {
  display: none;
}
.rn-close-button {
  display: block !important;
}

.badge {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--v-100);
  border-radius: 30px;
  border: solid 2px var(--v-300);
  color: var(--color-gray);
  font-size: 12px;
  width: 70px;
  margin-right: 4px;
  padding: 2px 4px;

  &--dark {
    background-color: #27262c;
    border-color: #6e4ce6;
  }
}
</style>
